import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { Shield, Smartphone, AlertTriangle, ToggleRight } from 'lucide-react'
import PhoneMockup from '../components/PhoneMockup'

const features = [
  {
    icon: <Shield size={22} />,
    title: '100% Number Masked',
    desc: 'Your real phone number is never shared. All communication goes through our secure proxy layer.',
    color: '#00e5a0',
  },
  {
    icon: <Smartphone size={22} />,
    title: 'Works Without App',
    desc: 'Anyone can scan and contact you — no app download required. Just scan, tap, done.',
    color: '#00e5a0',
  },
  {
    icon: <AlertTriangle size={22} />,
    title: 'Emergency Contacts',
    desc: 'Set a backup emergency number that only activates in critical situations you define.',
    color: '#ff6b35',
  },
  {
    icon: <ToggleRight size={22} />,
    title: 'Enable / Disable Anytime',
    desc: 'Going on vacation? Toggle off your tag in one tap. Re-enable with the same tap.',
    color: '#00e5a0',
  },
]

const stats = [
  { value: '3L+', label: 'Active Tags' },
  { value: '80K+', label: 'Downloads' },
  { value: '⭐ 4.9', label: 'Rating' },
]

export default function Landing() {
  const navigate = useNavigate()
  const { user } = useAuth()

  return (
    <div className="min-h-screen bg-bg text-brand-text font-sans">
      {/* Nav */}
      <nav className="sticky top-0 z-40 flex items-center justify-between px-5 py-4"
        style={{ background: 'rgba(10,10,15,0.85)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        <span className="font-syne font-extrabold text-xl text-brand-text">
          Park<span className="text-accent">Ping</span>
        </span>
        {user ? (
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-1.5 rounded-full text-xs font-semibold transition-all hover:opacity-90"
            style={{ background: '#00e5a0', color: '#0a0a0f' }}
          >
            My Dashboard →
          </button>
        ) : (
          <button
            onClick={() => navigate('/login')}
            className="px-4 py-1.5 rounded-full text-xs font-semibold transition-all hover:opacity-90"
            style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}
          >
            Sign In
          </button>
        )}
      </nav>

      <div className="mx-auto max-w-[480px] px-5">
        {/* Hero */}
        <section className="pt-10 pb-8 text-center">
          {/* Pill badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 text-sm font-semibold"
            style={{ background: 'rgba(0,229,160,0.1)', color: '#00e5a0', border: '1px solid rgba(0,229,160,0.25)' }}
          >
            <span className="w-2 h-2 rounded-full bg-accent animate-pulse inline-block" />
            1M+ active tags across India
          </div>

          <h1 className="font-syne font-extrabold text-4xl leading-tight mb-4 text-brand-text">
            Contact without{' '}
            <span className="text-accent">revealing</span>{' '}
            your number
          </h1>

          <p className="text-brand-muted text-base leading-relaxed mb-8 max-w-sm mx-auto">
            Stick a QR tag on your vehicle, pet collar, or child's bag. Anyone can call, WhatsApp or SMS you — all through a secure proxy. Your real number stays private, always.
          </p>

          {/* PhoneMockup */}
          <PhoneMockup />

          {/* CTA */}
          <div className="mt-10 space-y-3">
            <button
              onClick={() => navigate('/onboarding')}
              className="w-full py-4 rounded-2xl font-syne font-bold text-lg text-bg transition-all hover:opacity-90 active:scale-95"
              style={{ background: '#00e5a0', boxShadow: '0 0 24px rgba(0,229,160,0.4)' }}
            >
              Get Your Tag →
            </button>
            <p className="text-xs text-brand-muted">
              🔒 Free setup · No hidden fees · One-time purchase
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="py-6">
          <div
            className="grid grid-cols-3 gap-3 rounded-2xl p-4"
            style={{ background: '#13131a', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <p className="font-syne font-extrabold text-xl text-accent">{s.value}</p>
                <p className="text-[11px] text-brand-muted mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Features */}
        <section className="py-6 space-y-3">
          <h2 className="font-syne font-bold text-2xl text-brand-text mb-4 text-center">
            Why ParkPing?
          </h2>
          <div className="grid grid-cols-1 gap-3">
            {features.map((f) => (
              <div
                key={f.title}
                className="rounded-2xl p-4 flex gap-4"
                style={{
                  background: '#13131a',
                  border: `1px solid ${f.color}22`,
                }}
              >
                <div
                  className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: `${f.color}18`, color: f.color }}
                >
                  {f.icon}
                </div>
                <div>
                  <p className="font-semibold text-brand-text text-sm mb-1">{f.title}</p>
                  <p className="text-xs text-brand-muted leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-8 text-center">
          <div
            className="rounded-2xl p-6 space-y-4"
            style={{ background: 'linear-gradient(135deg, rgba(0,229,160,0.08) 0%, rgba(255,107,53,0.08) 100%)', border: '1px solid rgba(0,229,160,0.2)' }}
          >
            <h3 className="font-syne font-bold text-2xl text-brand-text">
              Ready to protect what matters?
            </h3>
            <p className="text-sm text-brand-muted">
              Vehicle, child, or pet — set up your private tag in under 2 minutes.
            </p>
            <button
              onClick={() => navigate('/onboarding')}
              className="w-full py-4 rounded-2xl font-syne font-bold text-lg text-bg transition-all hover:opacity-90 active:scale-95"
              style={{ background: '#00e5a0', boxShadow: '0 0 24px rgba(0,229,160,0.35)' }}
            >
              Get Your Tag Free →
            </button>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-6 text-center border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <p className="font-syne font-bold text-lg mb-1">
            Park<span className="text-accent">Ping</span>
          </p>
          <p className="text-xs text-brand-muted">
            Privacy-first parking contacts for India &copy; {new Date().getFullYear()}
          </p>
        </footer>
      </div>
    </div>
  )
}
